import { useEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { X, Plus, CalendarClock, Trash2 } from '@/components/icons'
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer'
import { Button } from './ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog'
import { EmptyState } from './EmptyState'
import { ListSkeleton } from './ui/skeleton-presets'
import { Pill } from './ui/pill'
import { useMaintenanceGetByAircraft, useMaintenanceDelete } from '../lib/hooks'
import { useIsAdmin } from '@/hooks/useIsCompanyAdmin'
import {
  AircraftMaintenanceDrawer,
  type MaintenanceRecordItem,
} from './AircraftMaintenanceDrawer'

interface AircraftMaintenanceHistoryDrawerProps {
  aircraftId: string
  open: boolean
  onClose: () => void
}

type MaintenanceStatus = 'Active' | 'Scheduled' | 'Completed'

function getMaintenanceStatus(record: MaintenanceRecordItem): MaintenanceStatus {
  const now = dayjs()
  const start = dayjs(record.startDate)
  const end = dayjs(record.endDate)
  if (now.isBefore(start)) return 'Scheduled'
  if (now.isBefore(end)) return 'Active' // [start, end)
  return 'Completed'
}

// Display labels are decoupled from the internal status keys: an "Active"
// maintenance window means the aircraft is currently in maintenance, so it
// reads as "Maintenance" rather than "Active" (which would imply available).
const STATUS_LABELS: Record<MaintenanceStatus, string> = {
  Active: 'Maintenance',
  Scheduled: 'Scheduled',
  Completed: 'Completed',
}

function MaintenanceStatusPill({ status }: { status: MaintenanceStatus }) {
  const label = STATUS_LABELS[status]
  if (status === 'Active') return <Pill variant="warning">{label}</Pill>
  if (status === 'Scheduled') return <Pill variant="info">{label}</Pill>
  return <Pill variant="draft">{label}</Pill>
}

export function AircraftMaintenanceHistoryDrawer({
  aircraftId,
  open,
  onClose,
}: AircraftMaintenanceHistoryDrawerProps) {
  const { isAdmin } = useIsAdmin()
  const { data: records = [], isPending: loading } =
    useMaintenanceGetByAircraft(aircraftId)
  const { mutate: deleteMaintenance } = useMaintenanceDelete(aircraftId)

  const [formOpen, setFormOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<MaintenanceRecordItem | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<MaintenanceRecordItem | null>(null)

  const scrollRef = useRef<HTMLDivElement>(null)
  const closestRef = useRef<HTMLDivElement>(null)

  // The closest record to surface: the first ongoing/upcoming window (records
  // arrive oldest-first), or the most recent one when every window is past.
  const closestIndex = useMemo(() => {
    const list = records as MaintenanceRecordItem[]
    const idx = list.findIndex((r) => getMaintenanceStatus(r) !== 'Completed')
    return idx === -1 ? list.length - 1 : idx
  }, [records])

  // Once rendered, scroll so the closest record sits at the top of the list,
  // leaving past windows reachable by scrolling up.
  useEffect(() => {
    if (!open || loading) return
    const container = scrollRef.current
    const target = closestRef.current
    if (!container || !target) return
    container.scrollTop +=
      target.getBoundingClientRect().top - container.getBoundingClientRect().top
  }, [open, loading, closestIndex])

  const openCreate = () => {
    setEditingRecord(null)
    setFormOpen(true)
  }

  const openEdit = (record: MaintenanceRecordItem) => {
    if (!isAdmin) return
    setEditingRecord(record)
    setFormOpen(true)
  }

  const handleDelete = () => {
    if (!deleteTarget) return
    deleteMaintenance(
      { id: deleteTarget.id },
      {
        onSuccess: () => {
          toast.success('Maintenance removed')
          setDeleteTarget(null)
        },
        onError: () => {
          toast.error('Failed to remove maintenance')
          setDeleteTarget(null)
        },
      }
    )
  }

  return (
    <>
      <Drawer open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DrawerContent className="bg-surface-L0 max-h-[85dvh]">
          <DrawerHeader className="flex items-center justify-between p-4">
            <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
              Maintenance
            </DrawerTitle>
            <Button
              variant="secondary"
              size="icon"
              roundness="round"
              className="size-8"
              onClick={onClose}
            >
              <X className="w-4 h-4" />
            </Button>
          </DrawerHeader>

          {/* Content */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 pb-2"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {loading ? (
              <ListSkeleton />
            ) : records.length === 0 ? (
              <EmptyState
                icon={CalendarClock}
                title="No maintenance"
                description="Scheduled maintenance windows for this aircraft will appear here."
                addButtonText={isAdmin ? 'Schedule Maintenance' : undefined}
                onAdd={isAdmin ? openCreate : undefined}
              />
            ) : (
              <div className="flex flex-col gap-2 py-2">
                {(records as MaintenanceRecordItem[]).map((record, index) => {
                  const status = getMaintenanceStatus(record)
                  return (
                    <div
                      key={record.id}
                      ref={index === closestIndex ? closestRef : undefined}
                      className="flex items-center gap-3 rounded-2xl border border-border-L1 bg-surface-L1 px-4 py-2"
                    >
                      <button
                        type="button"
                        onClick={() => openEdit(record)}
                        disabled={!isAdmin}
                        className="flex flex-col items-start flex-1 min-w-0 text-left disabled:cursor-default"
                      >
                        <div className="flex items-center gap-2 py-1 text-sm text-content-primary-bold leading-5">
                          <span>{dayjs.utc(record.startDate).format('MMM D YYYY')}</span>
                          <span>-</span>
                          <span>{dayjs.utc(record.endDate).format('MMM D YYYY')}</span>
                        </div>
                        <MaintenanceStatusPill status={status} />
                      </button>

                      <div className="flex min-w-[70px] shrink-0 items-center justify-center rounded-lg bg-surface-L2 p-2">
                        <span className="text-base font-semibold text-content-primary-bold leading-6 tracking-[-0.5px] whitespace-nowrap">
                          {record.durationDays} {record.durationDays === 1 ? 'Day' : 'Days'}
                        </span>
                      </div>

                      {isAdmin && (
                        <Button
                          type="button"
                          variant="destructive"
                          size="mini"
                          className="shrink-0"
                          onClick={() => setDeleteTarget(record)}
                          aria-label="Remove maintenance period"
                        >
                          <Trash2 />
                        </Button>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {isAdmin && records.length > 0 && (
            <DrawerFooter>
              <Button
                type="button"
                variant="primary"
                size="sm"
                className="w-full"
                onClick={openCreate}
              >
                <Plus className="size-4" />
                Schedule Maintenance
              </Button>
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>

      {/* Create / edit form (stacked sub-drawer) */}
      <AircraftMaintenanceDrawer
        aircraftId={aircraftId}
        open={formOpen}
        record={editingRecord}
        onClose={() => setFormOpen(false)}
      />

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(isOpen) => !isOpen && setDeleteTarget(null)}
      >
        <AlertDialogContent className="bg-surface-L1">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Maintenance Period?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the scheduled maintenance window. If it's currently
              active, the aircraft's status reverts.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-content-negative-bold hover:bg-content-negative-bold/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
